import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Button from "../components/common/Button";
import Confetti from 'react-confetti';
import useGET from "../services/useGET";

export default function ActualQuiz() {
    const location = useLocation();
    const { speciesOut, areaName } = location.state || { speciesOut: {}, areaName: "Test Area" };
    const history = useHistory();
    
    const [currentQuestionNumber, setCurrentQuestionNumber] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const [score, setScore] = useState(0);
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [timeRemaining, setTimeRemaining] = useState(1500); // 25 minutes in seconds

    const { data: allQuestions } = useGET(`${process.env.REACT_APP_API_URL}/api/questions`);

    // Shuffle array helper function
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Filter questions based on selected species
    useEffect(() => {
        if (allQuestions && speciesOut) {
            const selectedSpeciesIds = Object.keys(speciesOut).filter(id => speciesOut[id]);
            // Always include Allemansrätten questions (species_id 1500)
            const questions = allQuestions.filter(q => 
                selectedSpeciesIds.includes(String(q.species_id)) || q.species_id === 1500
            );
            // Shuffle questions so order is random each time
            const shuffledQuestions = shuffleArray(questions);
            setFilteredQuestions(shuffledQuestions);
            setIsLoading(false);
        }
    }, [allQuestions, speciesOut]);

    // Countdown timer - auto-submit when time runs out
    useEffect(() => {
        if (showResults || isLoading) return; // Don't run timer on results screen or while loading

        const timer = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    calculateResults(); // Auto-submit when time is up
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer); // Clean up on unmount (leaving page)
    }, [showResults, isLoading]);

    const currentQuestion = filteredQuestions[currentQuestionNumber];

    const shuffledAnswers = useMemo(() => {
        if (!currentQuestion) return [];
        const answers = [
            { label: 'A', text: currentQuestion.correct_answer, color: 'bg-blue-100 border-blue-300 hover:border-blue-500' },
            { label: 'B', text: currentQuestion.wrong_answer1, color: 'bg-green-100 border-green-300 hover:border-green-500' },
            { label: 'C', text: currentQuestion.wrong_answer2, color: 'bg-yellow-100 border-yellow-300 hover:border-yellow-500' }
        ];
        // Shuffle the answers
        for (let i = answers.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [answers[i], answers[j]] = [answers[j], answers[i]];
        }
        // Re-assign labels A, B, C after shuffling
        return answers.map((answer, index) => ({
            ...answer,
            label: String.fromCharCode(65 + index) // 65 is 'A' in ASCII
        }));
    }, [currentQuestion]);

    const handleAnswerSelect = (answer) => {
        if (!currentQuestion) return;
        const key = currentQuestion.id;
        setUserAnswers({ ...userAnswers, [key]: answer });
        setSelectedAnswer(answer);
    };

    const handlePreviousQuestion = () => {
        if (currentQuestionNumber > 0) {
            setCurrentQuestionNumber(currentQuestionNumber - 1);
            setSelectedAnswer(userAnswers[filteredQuestions[currentQuestionNumber - 1].id] || null);
        }
    };

    const handleNextQuestion = () => {
        if (currentQuestionNumber < filteredQuestions.length - 1) {
            setCurrentQuestionNumber(currentQuestionNumber + 1);
            setSelectedAnswer(userAnswers[filteredQuestions[currentQuestionNumber + 1].id] || null);
        } else {
            calculateResults();
        }
    };

    const calculateResults = () => {
        let correctCount = 0;
        filteredQuestions.forEach((question) => {
            if (userAnswers[question.id] === question.correct_answer) {
                correctCount++;
            }
        });
        setScore(correctCount);
        setShowResults(true);
    };

    if (isLoading) {
        return (
            <div className="flex flex-col min-h-screen bg-offwhite font-body">
                <Navbar />
                <main className="relative w-full flex items-center justify-center" style={{ height: 'calc(100vh - 56px)', marginTop: '56px' }}>
                    <div className="text-center p-8">
                        <h2 className="font-heading text-2xl text-charcoal mb-4">Laddar frågor...</h2>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (filteredQuestions.length === 0) {
        return (
            <div className="flex flex-col min-h-screen bg-offwhite font-body">
                <Navbar />
                <main className="relative w-full flex items-center justify-center" style={{ height: 'calc(100vh - 56px)', marginTop: '56px' }}>
                    <div className="text-center p-8">
                        <h2 className="font-heading text-2xl text-charcoal mb-4">Inga frågor tillgängliga</h2>
                        <Button variant="default" onClick={() => history.push("/QuizPage")}>
                            Tillbaka till val
                        </Button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-offwhite font-body">
            <Navbar />

            {showResults ? (
                // Results Screen - Scrollable
                <main className="relative w-full overflow-y-auto" style={{ height: 'calc(100vh - 56px)', marginTop: '56px' }}>
                    <div className="container mx-auto px-4 py-8 max-w-4xl">
                        {score === filteredQuestions.length && (
                            <Confetti width={window.innerWidth} height={window.innerHeight} />
                        )}
                        
                        <div className="bg-white rounded-lg shadow-lg p-8 border border-sage">
                            <h1 className="font-heading text-4xl font-bold text-center text-forest mb-4">
                                Ditt resultat
                            </h1>
                            <p className="font-body text-2xl text-center text-charcoal mb-8">
                                Du fick <span className="font-bold text-forest">{score}</span> av <span className="font-bold">{filteredQuestions.length}</span> rätt!
                            </p>

                            {/* Results breakdown */}
                            <div className="space-y-4 mb-8 max-h-96 overflow-y-auto">
                                {filteredQuestions.map((question, index) => {
                                    const isCorrect = userAnswers[question.id] === question.correct_answer;
                                    return (
                                        <div
                                            key={question.id}
                                            className={`p-4 rounded-lg border-2 ${
                                                isCorrect ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
                                            }`}
                                        >
                                            <p className="font-semibold text-charcoal mb-2">
                                                {index + 1}. {question.question}
                                            </p>
                                            <p className={`text-sm ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                                {isCorrect ? '✓ Rätt!' : `✗ Fel, ditt svar var: ${userAnswers[question.id] || 'Inget svar'}`}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Action buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button variant="default" onClick={() => history.go(0)}>
                                    Starta om
                                </Button>
                                <Button variant="secondary" onClick={() => history.push("/")}>
                                    Till startsidan
                                </Button>
                            </div>
                        </div>
                    </div>
                </main>
            ) : (
                // Quiz Section - Full viewport height
                <main className="relative w-full bg-offwhite" style={{ minHeight: 'calc(100vh - 56px)', marginTop: '56px' }}>
                    <div className="w-full h-full flex flex-col px-4 py-4 lg:px-8 lg:py-6 mx-auto" style={{ maxWidth: '950px' }}>
                        {/* Timer */}
                        <div className="mb-3 lg:mb-4 flex-shrink-0 text-center">
                            <div className={`inline-block px-6 py-3 rounded-lg font-heading text-2xl font-bold ${
                                timeRemaining <= 10 ? 'bg-red-100 text-red-700 border-2 border-red-500' : 'bg-white text-charcoal border-2 border-sage'
                            }`}>
                                <span className="text-sm font-body">Tid kvar: </span>
                                {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-3 lg:mb-4 flex-shrink-0">
                            <div className="flex justify-between items-center mb-2">
                                <span className="font-body text-sm text-charcoal">
                                    Framsteg
                                </span>
                                <span className="font-body text-sm font-bold text-forest">
                                    {currentQuestionNumber + 1} / {filteredQuestions.length}
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700 h-4">
                                <div
                                    className="bg-forest h-4 rounded-full transition-all duration-300"
                                    style={{ width: `${((currentQuestionNumber + 1) / filteredQuestions.length) * 100}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* Quiz Card */}
                        <div className="bg-white rounded-lg shadow-lg border border-sage overflow-hidden flex flex-col flex-grow">
                            {/* Question Header */}
                            <div className="bg-sage py-4 px-4 lg:py-5 lg:px-8 h-20 lg:h-24 flex items-center justify-center">
                                <h2 className="font-heading text-lg md:text-xl lg:text-2xl text-center text-charcoal">
                                    {currentQuestion?.question}
                                </h2>
                            </div>

                            {/* Image Section */}
                            <div className="flex-grow flex items-center justify-center p-4 sm:p-6 lg:p-8 bg-white min-h-0">
                                <div className="relative rounded-lg overflow-hidden shadow-md bg-white w-full h-full max-h-full flex items-center justify-center">
                                    {currentQuestion?.image && (
                                        <img
                                            src={`${process.env.REACT_APP_API_URL}/storage/questions/${currentQuestion.image}`}
                                            alt="Quiz question"
                                            className="max-w-full max-h-full object-contain"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://media.discordapp.net/attachments/1185370412880764988/1438134763738366022/But_It27s_Honest_Work.png?ex=6915c6de&is=6914755e&hm=8f667cd28a805037163ea55d832a82c07987635719fe80d360d493b8aca90e7f&=&format=webp&quality=lossless';
                                            }}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Answer Options */}
                            <div className="px-4 py-3 lg:px-8 lg:py-5 space-y-2 lg:space-y-3 flex-shrink-0">
                                {shuffledAnswers.map((answer) => (
                                    <button
                                        key={answer.label}
                                        onClick={() => handleAnswerSelect(answer.text)}
                                        className={`w-full p-3 lg:p-4 rounded-lg border-2 text-left transition-all duration-200 flex items-center gap-3 lg:gap-4 ${
                                            selectedAnswer === answer.text
                                                ? 'bg-forest text-white border-forest shadow-lg transform scale-[1.02]'
                                                : `${answer.color} text-charcoal hover:shadow-md`
                                        }`}
                                    >
                                        <span className={`font-bold text-base lg:text-lg px-2 py-1 lg:px-3 lg:py-2 rounded min-w-[35px] lg:min-w-[45px] text-center ${
                                            selectedAnswer === answer.text
                                                ? 'bg-white text-forest'
                                                : 'bg-white text-charcoal'
                                        }`}>
                                            {answer.label}
                                        </span>
                                        <span className="font-body text-sm md:text-base lg:text-lg">
                                            {answer.text}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {/* Navigation Buttons */}
                            <div className="px-4 py-3 lg:px-8 lg:py-4 bg-white flex justify-between gap-4 flex-shrink-0">
                                <button
                                    onClick={handlePreviousQuestion}
                                    disabled={currentQuestionNumber === 0}
                                    className={`px-5 py-3 lg:px-6 lg:py-3 rounded-lg font-body text-sm lg:text-base font-semibold transition-all duration-200 ${
                                        currentQuestionNumber === 0
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-white text-charcoal border-2 border-sage hover:bg-sage hover:shadow-md'
                                    }`}
                                >
                                    ← Föregående
                                </button>
                                <button
                                    onClick={handleNextQuestion}
                                    className="px-5 py-3 lg:px-6 lg:py-3 rounded-lg font-body text-sm lg:text-base font-semibold bg-forest text-white hover:bg-leaf transition-all duration-200 hover:shadow-lg"
                                >
                                    {currentQuestionNumber === filteredQuestions.length - 1
                                        ? 'Skicka in'
                                        : 'Nästa →'}
                                </button>
                            </div>
                        </div>
                    </div>
                </main>
            )}

            <Footer />
        </div>
    );
}
